<script lang="ts">
    // Bloco "Serviços" da folha do agendamento (v4). Mesma regra de negócio do bloco antigo
    // (appointment-form-block.svelte): cada item é um serviço com um atendente, e adicionar um item
    // recalcula o término automático, os recursos e o consumo previsto pela receita.
    import ZentitySelect from '../../../_common/z/zentity-select.svelte'
    import { createEventDispatcher } from 'svelte'
    import Fa from 'svelte-fa'
    import { faBellConcierge, faCheck, faPlus, faTrashAlt } from '@fortawesome/free-solid-svg-icons'
    import { slide } from 'svelte/transition'
    import type { Appointment } from '../../../../modules/schedulerApi/appointments/appointment'
    import { AppointmentItem } from '../../../../modules/schedulerApi/appointments/appointment-item'
    import type { AppointmentSugestion } from '../../../../modules/schedulerApi/appointments/appointmentSugestion'
    import type { Schedule } from '../../../../modules/schedulerApi/schedules/schedule'
    import type { Service } from '../../../../modules/posApi/services/service'
    import { AppointmentStatus } from '../../../../modules/schedulerApi/appointments/appointments.enum'
    import type { User } from '../../../../modules/usersApi/users/user'
    import ServiceSelect_2 from '../../../pos/service-select-2.svelte'
    import AppointmentFormBlockSugestions from '../../appointment-form-block-sugestions.svelte'
    import ZsheetSection from '../../../_common/z/sheet/zsheet-section.svelte'
    import ZsheetEntity from '../../../_common/z/sheet/zsheet-entity.svelte'
    import ZsheetField from '../../../_common/z/sheet/zsheet-field.svelte'

    const dispatch = createEventDispatcher()

    export let entity: Appointment
    export let schedule: Schedule = null
    export let personId: string = null

    let sugest

    // linha em edição: um item da lista, ou 'new' enquanto o atendente monta um item novo
    let editing: AppointmentItem | 'new' = null
    let draftUser: User = null
    let draftService: Service = null
    let draftParentServicesOnInvoicesId: string = null
    let draftGroupId: string = null
    let draftInvoiceId: string = null
    let lastUserId: string = null

    // status em que o serviço não é descontado do saldo do cliente (mesma lista do bloco antigo)
    const statusWithNoOnus = [
        AppointmentStatus.agendamento_remarcado,
        AppointmentStatus.agendamento_cancelado_empresa,
        AppointmentStatus.agendamento_cancelado_usuario,
    ]

    $: items = (entity?.items || []).filter((el) => !!el)
    $: totalMinutes = items.reduce((acc, el) => acc + (el.service?.duration || 0), 0)
    $: sectionMeta = items.length === 0 ? '' : `${items.length} ${items.length === 1 ? 'item' : 'itens'} · ${durationLabel(totalMinutes)}`

    function durationLabel(minutes: number) {
        if (!minutes) return '-'
        const h = Math.floor(minutes / 60)
        const m = minutes % 60
        if (h === 0) return `${m} min`
        if (m === 0) return `${h}h`
        return `${h}h${String(m).padStart(2, '0')}`
    }

    function itemSubtitle(item: AppointmentItem) {
        const parts = []
        if (item.service?.duration) parts.push(durationLabel(item.service.duration))
        parts.push(item.user?.userName || item.user?.email || '[sem atendente]')
        return parts.join(' · ')
    }

    function setEditing(value: AppointmentItem | 'new') {
        editing = value
        dispatch('state', value ? 'typing' : 'ready')
    }

    function startNew() {
        draftUser = lastUserId ? ({ userId: lastUserId } as User) : null
        draftService = null
        draftParentServicesOnInvoicesId = null
        draftGroupId = null
        draftInvoiceId = null
        setEditing('new')
    }

    function startEdit(item: AppointmentItem) {
        draftUser = item.user
        draftService = item.service
        draftParentServicesOnInvoicesId = item.parentServicesOnInvoicesId
        draftGroupId = item.groupId
        draftInvoiceId = item.invoiceId
        setEditing(item)
    }

    function cancelEdit() {
        setEditing(null)
    }

    function validateDraft() {
        if (!draftUser) {
            window.error('Informe o atendente')
            return false
        }
        if (!draftService) {
            window.error('Informe o serviço')
            return false
        }
        return true
    }

    async function confirmEdit() {
        if (!validateDraft()) return
        if (editing === 'new') {
            await addItem(draftUser, draftService, draftParentServicesOnInvoicesId, draftInvoiceId, draftGroupId)
        } else {
            await updateItem(editing as AppointmentItem, draftUser, draftService)
        }
        setEditing(null)
    }

    // idêntico ao addNewItem do bloco antigo: comissão é só uma prévia local (o backend recalcula)
    async function addItem(user: User, service: Service, parentServicesOnInvoicesId: string, invoiceId: string, groupId: string) {
        const item = new AppointmentItem()
        item.service = (await window.api.utils.utils.assignServiceCategoryFromObject([service]))[0]
        item.invoiceId = invoiceId
        item.user = user
        item.label = item.service.name
        item.serviceId = item.service.serviceId
        item.userId = item.user?.userId
        item.parentServicesOnInvoicesId = parentServicesOnInvoicesId
        item.comissionCosts = item.service.comissionCostsAttendant
        item.comissionFixed = item.service.comissionFixedAttendant
        item.price = item.service.price
        item.comissionPercentage = (item.price * item.service.comissionPercentageAttendant) / 100
        entity.items = [...entity.items, item]
        lastUserId = item.userId
        await autoFillConsumptionFromRecipe(item)
        sugest?.addLastService()
        dispatch('change')
    }

    // preenchimento automático de consumo (BOM) — só na primeira adição do item, nunca ao editar
    async function autoFillConsumptionFromRecipe(item: AppointmentItem) {
        const added = await window.api.utils.utils.buildConsumptionFromItems([item])
        if (added.length === 0) return
        if (!entity.consumption) entity.consumption = []
        entity.consumption = [...entity.consumption, ...added]
    }

    async function updateItem(item: AppointmentItem, user: User, service: Service) {
        item.user = user
        item.service = service
        item.label = service.name
        item.serviceId = service.serviceId
        item.userId = user.userId
        item.parentServicesOnInvoicesId = draftParentServicesOnInvoicesId
        item.invoiceId = draftInvoiceId
        item.comissionCosts = service.comissionCostsAttendant
        item.comissionFixed = service.comissionFixedAttendant
        item.price = service.price
        item.comissionPercentage = (item.price * service.comissionPercentageAttendant) / 100
        entity.items = entity.items
        lastUserId = item.userId
        dispatch('change')
    }

    function removeItem(item: AppointmentItem) {
        entity.items = entity.items.filter((el) => el !== item)
        if (editing === item) setEditing(null)
        dispatch('change')
    }

    async function onAddSugestion(ev) {
        const sugestion: AppointmentSugestion = ev.detail
        await addItem(sugestion.user, sugestion.service, sugestion.parentServicesOnInvoicesId, sugestion.invoiceId, sugestion.groupId)
    }
</script>

<ZsheetSection label="Serviços" meta={sectionMeta}>
    <div class="services">
        {#each items as item (item)}
            <div transition:slide={{ duration: 160 }}>
                {#if editing === item}
                    <div class="edit">
                        <ZsheetField label="Atendente">
                            <ZentitySelect fonte="usuario" somenteIds={schedule?.attendants} bind:value={draftUser}></ZentitySelect>
                        </ZsheetField>
                        <ZsheetField label="Serviço">
                            <ServiceSelect_2
                                v2={true}
                                onlyWithBalance={window.configs.schedulueOnlyWithBalanceOrNoValue}
                                duration={true}
                                price={false}
                                small={true}
                                bind:service={draftService}
                                bind:balance_parentServicesOnInvoicesId={draftParentServicesOnInvoicesId}
                                bind:balanceResultId={draftGroupId}
                                bind:invoiceId={draftInvoiceId}
                                balance={true}
                                scheduleId={entity?.scheduleId}
                                {personId}
                            />
                        </ZsheetField>
                        <div class="edit_acts">
                            <button type="button" class="act act-ghost" on:click={cancelEdit}>Cancelar</button>
                            <button type="button" class="act act-danger" on:click={() => removeItem(item)}>
                                <Fa icon={faTrashAlt} /> Remover
                            </button>
                            <div class="spacer"></div>
                            <button type="button" class="act act-primary" on:click={confirmEdit}><Fa icon={faCheck} /> Confirmar</button>
                        </div>
                    </div>
                {:else}
                    <ZsheetEntity
                        icon={faBellConcierge}
                        title={item.service?.name || '[serviço não cadastrado]'}
                        subtitle={itemSubtitle(item)}
                        clickable={true}
                        removable={true}
                        removeTitle="Remover serviço"
                        on:click={() => startEdit(item)}
                        on:remove={() => removeItem(item)}
                    >
                        {#if item.parentServicesOnInvoicesId}
                            <span class="balance-tag">
                                {statusWithNoOnus.includes(entity.status) ? 'não consumido do saldo' : 'consumido do saldo'}
                            </span>
                        {/if}
                    </ZsheetEntity>
                {/if}
            </div>
        {/each}

        {#if editing === 'new'}
            <div class="edit" transition:slide={{ duration: 160 }}>
                <ZsheetField label="Atendente">
                    <ZentitySelect fonte="usuario" somenteIds={schedule?.attendants} bind:value={draftUser}></ZentitySelect>
                </ZsheetField>
                <ZsheetField label="Serviço">
                    <ServiceSelect_2
                        v2={true}
                        onlyWithBalance={window.configs.schedulueOnlyWithBalanceOrNoValue}
                        duration={true}
                        price={false}
                        small={true}
                        bind:service={draftService}
                        bind:balance_parentServicesOnInvoicesId={draftParentServicesOnInvoicesId}
                        bind:balanceResultId={draftGroupId}
                        bind:invoiceId={draftInvoiceId}
                        balance={true}
                        scheduleId={entity?.scheduleId}
                        {personId}
                    />
                </ZsheetField>
                <div class="edit_acts">
                    <button type="button" class="act act-ghost" on:click={cancelEdit}>Cancelar</button>
                    <div class="spacer"></div>
                    <button type="button" class="act act-primary" on:click={confirmEdit}><Fa icon={faCheck} /> Adicionar</button>
                </div>
            </div>
        {:else}
            <div class="sugestions-wrap">
                <AppointmentFormBlockSugestions bind:this={sugest} {entity} {schedule} on:add={onAddSugestion}></AppointmentFormBlockSugestions>
            </div>
            <button type="button" class="add" on:click={startNew} disabled={!personId}>
                <Fa icon={faPlus} /> Serviço
            </button>
            {#if !personId}
                <div class="hint">Escolha o cliente para adicionar serviços.</div>
            {/if}
        {/if}
    </div>
</ZsheetSection>

<style>
    /* valores base = tema escuro */
    .services {
        display: flex;
        flex-direction: column;
        gap: 6px;
    }
    .balance-tag {
        display: inline-block;
        font-size: 11px;
        font-weight: 600;
        padding: 1px 7px;
        border-radius: 999px;
        background: #5b8def26;
        color: #8fb0f5;
        margin-top: 3px;
    }
    .edit {
        display: flex;
        flex-direction: column;
        gap: 10px;
        padding: 10px;
        border-radius: 10px;
        background: #ffffff0a;
        border: 1px solid #2ecc7144;
    }
    .edit_acts {
        display: flex;
        align-items: center;
        gap: 8px;
    }
    .spacer {
        flex: 1 1 auto;
    }
    .add,
    .act {
        border: 1px dashed #ffffff33;
        background: none;
        color: inherit;
        font: inherit;
        font-size: 14px;
        border-radius: 10px;
        padding: 10px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        min-height: 42px;
    }
    .add:disabled {
        opacity: 0.4;
    }
    .act {
        border-style: solid;
        border-color: #ffffff22;
        padding: 8px 12px;
    }
    .act-primary {
        background: #2ecc71;
        border-color: #2ecc71;
        color: #10331f;
        font-weight: 700;
    }
    .act-danger {
        color: #e58080;
        border-color: #e5737344;
    }
    .hint {
        font-size: 12px;
        opacity: 0.5;
    }

    /* as sugestões vêm do bloco antigo (appointment-form-block-sugestions.svelte), com o visual de lá.
       Aqui elas viram etiquetas discretas, no tom dos outros blocos. */
    .sugestions-wrap :global(.sugestions) {
        opacity: 1 !important;
        /* a folga embaixo é para a última etiqueta não encostar no que vem depois (o botão de
           adicionar, que tem borda tracejada e evidencia qualquer aperto) */
        padding: 2px 0px 6px 0px !important;
    }
    .sugestions-wrap :global(.sugestions_label) {
        font-size: 12.5px !important;
        opacity: 0.55;
    }
    .sugestions-wrap :global(.btn-sugestion) {
        border: 1px solid #ffffff22 !important;
        border-radius: 999px !important;
        color: inherit !important;
        font-size: 12.5px !important;
        overflow: hidden;
    }
    .sugestions-wrap :global(.btn-sugestion > div) {
        background: none !important;
        padding-top: 5px !important;
        padding-bottom: 5px !important;
    }
    .sugestions-wrap :global(.btn-sugestion > div:not(:first-child)) {
        opacity: 0.6;
    }

    :global(.theme_light) .add,
    :global(.theme_light) .act {
        border-color: #d8dbe0;
    }
    :global(.theme_light) .edit {
        background: #f7f8fa;
    }
    :global(.theme_light) .act-primary {
        color: #ffffff;
        border-color: #2ecc71;
    }
    :global(.theme_light) .act-danger {
        color: #c0392b;
        border-color: #f0c9c9;
    }
</style>
